import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import MainCard from "../../../../../components/MainCard";

import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableSimpleTable from "../../../../../components/ReusableComponent/ReusableSimpleTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { EyeOutlined, CloseOutlined } from "@ant-design/icons";
import { ResumeAdminNote } from "../../../../../types/resumes-managment/resume";
import ExpandingResumeAdminNoteDetail from "./ExpandingResumeAdminNoteDetail";
import AddEditAdminNoteForm from "./AddEditAdminNoteForm";

const ResumeAdminNotes = ({ resumeId }: { resumeId?: number }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );
  const [selectedAdminNote, setSelectedAdminNote] =
    useState<ResumeAdminNote | null>(null);
  const [selected, setSelected] = useState<any>("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResumeAdminNote[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<ResumeAdminNote>[]>(
    () => [
      // {
      //   header: () => <FormattedMessage id="ID" />,
      //   accessorKey: "id",
      //   meta: {
      //     className: "cell-center",
      //   },
      //   cell: ({ row }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">{row?.original.id}</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },
      {
        header: () => <FormattedMessage id="Resume ID" />,
        accessorKey: "resume_id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.resume_id}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Title" />,
        accessorKey: "admin_resume_note_type.title",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.admin_resume_note_type?.title || "-----"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Message" />,
        accessorKey: "admin_resume_note_type.message",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.admin_resume_note_type?.message?.length > 50
                  ? `${row?.original.admin_resume_note_type.message.substring(
                      0,
                      50
                    )}...`
                  : row?.original.admin_resume_note_type?.message || "-----"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "admin_resume_note_type.status",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.admin_resume_note_type?.status
                  ? "Active"
                  : "Inactive"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row">
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAdminNote(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/admin_notes/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Resume Admin Note deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to delete resume admin note",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(`/admin_notes`);
      const { data, meta } = response.data;
      setResult(data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching resume admin notes",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MainCard
          title="Resume Admin Notes"
          headerActions={
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setCustomerModal(true);
                setSelectedAdminNote(null);
              }}
            >
              Add Admin Note
            </Button>
          }
        >
          <ReusableSimpleTable
            data={result}
            columns={columns}
            renderDetailPanel={({ row }: any) => (
              <ExpandingResumeAdminNoteDetail data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </MainCard>
      )}
      <ReusableDeleteDialog
        title={selectedAdminNote?.admin_resume_note_type?.title}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <AddEditAdminNoteForm
        setResult={setResult}
        item={selectedAdminNote}
        open={customerModal}
        setOpen={setCustomerModal}
        resumeId={resumeId}
      />
    </>
  );
};

export default ResumeAdminNotes;
